import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Paper,
  Box,
  Breadcrumbs,
  Link,
  Alert,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Avatar,
  Drawer,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Logout,
  Home,
  Refresh,
  AccountCircle,
  ExpandMore,
  ViewList,
  ViewModule,
  Menu as MenuIcon,
  ChevronLeft
} from '@mui/icons-material';
import { useKeycloak } from '../contexts/KeycloakContext';
import { useApiClient } from '../services/apiClient';
import FolderTree from './FolderTree';
import FileList from './FileList';

const DRAWER_WIDTH = 300;

const S3Browser = ({ user }) => {
  const { logout } = useKeycloak();
  const apiClient = useApiClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [currentPath, setCurrentPath] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastLoadedPath, setLastLoadedPath] = useState('');
  const [loadingStartTime, setLoadingStartTime] = useState(null);
  const [requestCount, setRequestCount] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [drawerOpen, setDrawerOpen] = useState(!isMobile); // 데스크톱에서는 기본 열림, 모바일에서는 닫힘

  // 화면 크기 변경 시 드로어 상태 조정
  useEffect(() => {
    if (isMobile) {
      setDrawerOpen(false); // 모바일에서는 기본적으로 닫힘
    } else {
      setDrawerOpen(true); // 데스크톱에서는 기본적으로 열림
    }
  }, [isMobile]);

  const loadItems = async (path = '', isRetry = false) => {
    const now = Date.now();
    
    // 요청 카운트 증가 및 차단 확인
    const newRequestCount = requestCount + 1;
    setRequestCount(newRequestCount);
    
    // 10초 내에 10회 이상 요청 시 차단
    if (newRequestCount > 10) {
      console.error('Too many requests detected, blocking further calls');
      setIsBlocked(true);
      setError('너무 많은 요청이 감지되어 일시적으로 차단되었습니다. 페이지를 새로고침해주세요.');
      return;
    }
    
    // 이미 차단된 상태면 요청 거부
    if (isBlocked) {
      console.log('Requests are blocked due to excessive calls');
      return;
    }
    
    // 중복 호출 방지: 같은 경로를 1초 이내에 다시 로드하려고 하면 차단
    if (!isRetry && path === lastLoadedPath && loadingStartTime && (now - loadingStartTime) < 1000) {
      console.log('Duplicate loadItems call blocked for path:', path);
      return;
    }
    
    // 이미 재시도 중이거나 재시도 횟수가 3회를 초과하면 중단
    if (isRetrying || (!isRetry && retryCount >= 3)) {
      console.log('Skipping loadItems - already retrying or max retries reached');
      return;
    }

    setLoading(true);
    setError(null);
    setLastLoadedPath(path);
    setLoadingStartTime(now);
    
    if (isRetry) {
      setIsRetrying(true);
    }
    
    try {
      console.log('Loading items for path:', path, isRetry ? `(retry ${retryCount + 1})` : '', `(request #${newRequestCount})`);
      
      const result = await apiClient.listObjects(path);
      console.log('API response:', result);
      
      setItems(result.items || []);
      setRetryCount(0); // 성공 시 재시도 카운트 리셋
      
      // 성공 시 요청 카운트 리셋 (10초 후)
      setTimeout(() => {
        setRequestCount(0);
      }, 10000);
      
    } catch (err) {
      console.error('Error loading items:', err);
      
      // ERR_INSUFFICIENT_RESOURCES 오류 감지
      if (err.message.includes('ERR_INSUFFICIENT_RESOURCES') || err.message.includes('net::ERR_INSUFFICIENT_RESOURCES')) {
        console.error('Browser resource exhaustion detected, blocking further requests');
        setIsBlocked(true);
        setError('브라우저 리소스가 부족합니다. 페이지를 새로고침해주세요.');
        return;
      }
      
      if (err.message.includes('401') || err.message.includes('Unauthorized')) {
        setError('인증이 만료되었습니다. 다시 로그인해주세요.');
        setRetryCount(0); // 인증 오류는 재시도하지 않음
      } else if (err.message.includes('fetch') || err.message.includes('NetworkError')) {
        const newRetryCount = retryCount + 1;
        setRetryCount(newRetryCount);
        
        // 자동 재시도 완전 제거 - 수동으로만 재시도 가능
        if (newRetryCount < 3) {
          setError(`API 서버에 연결할 수 없습니다. "다시 시도" 버튼을 클릭해주세요. (${newRetryCount}/3)`);
        } else {
          setError('API 서버에 연결할 수 없습니다. 서버가 실행 중인지 확인하고 새로고침해주세요.');
        }
      } else {
        setError(`파일 목록을 불러오는데 실패했습니다: ${err.message}`);
        setRetryCount(0); // 기타 오류는 재시도하지 않음
      }
    } finally {
      setLoading(false);
      if (isRetry) {
        setIsRetrying(false);
      }
    }
  };

  const handleItemClick = (item) => {
    if (item.type === 'folder') {
      setCurrentPath(item.path);
      // 모바일에서 폴더 선택 시 드로어 닫기
      if (isMobile) {
        setDrawerOpen(false);
      }
    } else {
      handleDownload(item);
    }
  };

  const handleDownload = async (item) => {
    try {
      console.log('Downloading item:', item);
      
      const result = await apiClient.getDownloadUrl(item.path);
      console.log('Download URL generated:', result);
      
      // 새 탭에서 다운로드 URL 열기
      const link = document.createElement('a');
      link.href = result.downloadUrl;
      link.download = item.name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (err) {
      console.error('Error downloading file:', err);
      
      if (err.message.includes('401') || err.message.includes('Unauthorized')) {
        setError('인증이 만료되었습니다. 다시 로그인해주세요.');
      } else if (err.message.includes('fetch') || err.message.includes('NetworkError')) {
        setError('네트워크 연결을 확인하고 다시 시도해주세요.');
      } else {
        setError(`파일 다운로드에 실패했습니다: ${err.message}`);
      }
    }
  };

  const handleGetInfo = async (item) => {
    try {
      const info = await apiClient.getObjectInfo(item.path);
      console.log('Object info:', info);
      
      // 간단한 알림으로 정보 표시 (나중에 모달로 개선 가능)
      const formatFileSize = (size) => {
        if (!size) return '-';
        const units = ['B', 'KB', 'MB', 'GB'];
        let unitIndex = 0;
        let fileSize = size;
        
        while (fileSize >= 1024 && unitIndex < units.length - 1) {
          fileSize /= 1024;
          unitIndex++;
        }
        
        return `${fileSize.toFixed(1)} ${units[unitIndex]}`;
      };
      
      alert(`파일 정보:\n크기: ${formatFileSize(info.contentLength)}\n타입: ${info.contentType}\n수정일: ${new Date(info.lastModified).toLocaleString()}`);
      
    } catch (err) {
      console.error('Error getting object info:', err);
      
      if (err.message.includes('401') || err.message.includes('Unauthorized')) {
        setError('인증이 만료되었습니다. 다시 로그인해주세요.');
      } else if (err.message.includes('fetch') || err.message.includes('NetworkError')) {
        setError('네트워크 연결을 확인하고 다시 시도해주세요.');
      } else {
        setError(`파일 정보를 가져오는데 실패했습니다: ${err.message}`);
      }
    }
  };

  const navigateToPath = (path) => {
    setCurrentPath(path);
    // 모바일에서 경로 변경 시 드로어 닫기
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const getBreadcrumbs = () => {
    if (!currentPath) return [];
    
    const parts = currentPath.split('/').filter(part => part);
    const breadcrumbs = [];
    let accumulatedPath = '';
    
    parts.forEach((part, index) => {
      accumulatedPath += part + '/';
      breadcrumbs.push({
        name: part,
        path: accumulatedPath
      });
    });
    
    return breadcrumbs;
  };

  const getUserDisplayName = () => {
    if (user?.name) return user.name;
    if (user?.username) return user.username;
    if (user?.email) return user.email;
    return 'User';
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Load initial data - currentPath가 변경될 때만 실행
  useEffect(() => {
    // 차단된 상태면 실행하지 않음
    if (isBlocked) {
      console.log('useEffect blocked due to excessive requests');
      return;
    }
    
    console.log('useEffect triggered for currentPath:', currentPath);
    
    // 디바운스: 100ms 후에 실행
    const timeoutId = setTimeout(() => {
      loadItems(currentPath);
    }, 100);
    
    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, isBlocked]); // currentPath와 isBlocked만 의존성으로 사용

  // 드로어 내용
  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 드로어 헤더 (모바일에서만 표시) */}
      {isMobile && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Typography variant="h6">폴더 구조</Typography>
          <IconButton onClick={handleDrawerToggle}>
            <ChevronLeft />
          </IconButton>
        </Box>
      )}
      
      <FolderTree 
        currentPath={currentPath} 
        onPathChange={navigateToPath}
      />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* 헤더 */}
      <AppBar position="static">
        <Toolbar>
          {/* 모바일에서 햄버거 메뉴 버튼 */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            🗂️ [DEV] KLP DUMP Manager
          </Typography>
          
          {/* 사용자 정보 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              avatar={<Avatar sx={{ bgcolor: 'secondary.main' }}>{getUserInitials()}</Avatar>}
              label={getUserDisplayName()} 
              color="secondary" 
              variant="outlined"
              sx={{ 
                color: 'white', 
                borderColor: 'white',
                display: { xs: 'none', sm: 'flex' } // 작은 화면에서는 숨김
              }}
            />
            
            <Button
              color="inherit"
              onClick={handleUserMenuOpen}
              endIcon={<ExpandMore />}
              sx={{ ml: 1 }}
            >
              <AccountCircle />
            </Button>
            
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={handleUserMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem disabled>
                <Box>
                  <Typography variant="subtitle2">{getUserDisplayName()}</Typography>
                  {user?.email && (
                    <Typography variant="caption" color="text.secondary">
                      {user.email}
                    </Typography>
                  )}
                  {user?.roles && user.roles.length > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      역할: {user.roles.join(', ')}
                    </Typography>
                  )}
                </Box>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <Logout sx={{ mr: 1 }} />
                로그아웃
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* 메인 컨텐츠 */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* 네비게이션 드로어 */}
        <Drawer
          variant={isMobile ? 'temporary' : 'persistent'}
          open={drawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // 모바일 성능 향상
          }}
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              position: isMobile ? 'fixed' : 'relative',
              height: isMobile ? '100%' : 'auto',
              top: isMobile ? 0 : 'auto',
              zIndex: isMobile ? theme.zIndex.drawer : 'auto'
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* 오른쪽 파일 목록 */}
        <Box 
          sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            overflow: 'hidden',
            width: { xs: '100%', md: `calc(100% - ${drawerOpen ? DRAWER_WIDTH : 0}px)` }
          }}
        >
          {/* 브레드크럼 및 도구 모음 */}
          <Paper sx={{ p: 2, borderRadius: 0, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                {/* 데스크톱에서 폴더 토글 버튼 */}
                <IconButton
                  onClick={handleDrawerToggle}
                  sx={{ mr: 1, display: { xs: 'none', md: 'inline-flex' } }}
                  title={drawerOpen ? '폴더 패널 숨기기' : '폴더 패널 보이기'}
                >
                  <MenuIcon />
                </IconButton>

                <Breadcrumbs sx={{ minWidth: 0, flex: 1 }}>
                  <Link
                    component="button"
                    variant="body1"
                    onClick={() => navigateToPath('')}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <Home sx={{ mr: 0.5 }} fontSize="inherit" />
                    Root
                  </Link>
                  {getBreadcrumbs().map((crumb, index) => (
                    <Link
                      key={index}
                      component="button"
                      variant="body1"
                      onClick={() => navigateToPath(crumb.path)}
                      sx={{ 
                        maxWidth: '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {crumb.name}
                    </Link>
                  ))}
                </Breadcrumbs>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                <IconButton 
                  onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
                  title={viewMode === 'list' ? '그리드 보기' : '목록 보기'}
                  sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                >
                  {viewMode === 'list' ? <ViewModule /> : <ViewList />}
                </IconButton>
                
                <IconButton onClick={() => loadItems(currentPath)} title="새로고침">
                  <Refresh />
                </IconButton>
              </Box>
            </Box>
            
            {/* 현재 경로 표시 */}
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              경로: /{currentPath || ''}
            </Typography>
          </Paper>

          {/* 에러 메시지 */}
          {error && (
            <Alert 
              severity={isBlocked ? "warning" : "error"}
              sx={{ m: 2, borderRadius: 1 }}
              action={
                <Box>
                  {isBlocked && (
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => window.location.reload()}
                    >
                      새로고침
                    </Button>
                  )}
                  {!isBlocked && (error.includes('서버가 실행') || error.includes('연결할 수 없습니다') || error.includes('네트워크')) && (
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={() => {
                        setRetryCount(0);
                        setError(null);
                        setRequestCount(0); // 요청 카운트도 리셋
                        loadItems(currentPath);
                      }}
                      disabled={loading || isRetrying}
                    >
                      다시 시도
                    </Button>
                  )}
                </Box>
              }
            >
              {error}
              {!isBlocked && retryCount > 0 && retryCount < 3 && (
                <Box sx={{ mt: 1 }}>
                  재시도 중... ({retryCount}/3)
                </Box>
              )}
              {isBlocked && (
                <Box sx={{ mt: 1 }}>
                  <strong>무한 루프가 감지되어 요청이 차단되었습니다.</strong>
                </Box>
              )}
            </Alert>
          )}

          {/* 파일 목록 */}
          <Box sx={{ flex: 1, overflow: 'hidden', p: 2 }}>
            <FileList
              items={items}
              onItemClick={handleItemClick}
              onDownload={handleDownload}
              onGetInfo={handleGetInfo}
              loading={loading}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default S3Browser;
